import { useQuery } from '@tanstack/react-query'

import { ATSJob } from '@pkg/types/api'

import api from '@/api'

export const ATS_JOBS_KEY = 'ats-jobs'

const MINUTE = 1000 * 60

export interface UseListATSJobsOptions {
  enabled?: boolean
  staleTime?: number
}

/**
 * Hook to list jobs from the user's connected ATS using the ATS
 * @param name - Optional job name/title to filter by
 * @param options - Query options including enabled state and stale time
 * @returns Query result with jobs data
 */
export const useListATSJobs = (
  name?: string,
  options: UseListATSJobsOptions = {},
) => {
  const { enabled = true, staleTime = 5 * MINUTE } = options

  return useQuery({
    queryKey: [ATS_JOBS_KEY, 'list', name || 'all'],
    queryFn: async (): Promise<ATSJob[]> => {
      const response = await api.listATSJobs(name)
      return response.data.jobs
    },
    enabled,
    staleTime,
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Helper function to get the display title for a job
 * @param job - The ATS job object
 * @returns The job title or a fallback string
 */
export const getATSJobTitle = (job: ATSJob): string => {
  return job.title || 'Untitled Position'
}

/**
 * Helper function to check if a job is active based on its status
 * @param job - The ATS job object
 * @returns True if the job status indicates it's active
 */
export const isATSJobActive = (job: ATSJob): boolean => {
  const activeStatuses = ['open', 'active', 'published', 'live']
  return activeStatuses.includes(job.status?.toLowerCase() || '')
}

/**
 * Helper function to format job location for display
 * @param job - The ATS job object
 * @returns Formatted location string or 'Remote' if no location
 */
export const getATSJobLocation = (job: ATSJob): string => {
  if (
    !job.location ||
    !Array.isArray(job.location) ||
    job.location.length === 0
  ) {
    return 'Remote'
  }

  const firstLocation = job.location[0]
  const parts = []

  if (firstLocation.city) parts.push(firstLocation.city)
  if (firstLocation.region) parts.push(firstLocation.region)

  return parts.length > 0 ? parts.join(', ') : 'Remote'
}

/**
 * Helper function to get job department for display
 * @param job - The ATS job object
 * @returns First department or null
 */
export const getATSJobDepartment = (job: ATSJob): string | null => {
  if (
    !job.department ||
    !Array.isArray(job.department) ||
    job.department.length === 0
  ) {
    return null
  }
  return job.department[0]
}

/**
 * Helper function to get job employment type for display
 * @param job - The ATS job object
 * @returns Employment type or 'Full-time' as default
 */
export const getATSJobEmploymentType = (job: ATSJob): string => {
  return job.employmentType || 'Full-time'
}
